import { photoPath } from '@baby-tracker/contracts';
import { calendarDateInZone, type DoseUnit, type ReminderKind } from '@baby-tracker/validation';
import { useState, type ReactElement, type SubmitEvent } from 'react';

import { usePhotos } from '../../../hooks/useMedia.js';
import {
    useCreateDose,
    useCreateMedication,
    useCreateMilestone,
    useCreateReminder,
    useDeleteDose,
    useDeleteMilestone,
    useDoses,
    useFamilyExport,
    useMedications,
    useMilestones,
    useReminders,
    useUpdateReminder,
} from '../../../hooks/useRecords.js';
import { formatWhen } from '../../../lib/activity-format.js';
import {
    formatCalendarDate,
    milestoneCategoryLabels,
    reminderKindLabels,
} from '../../../lib/labels.js';
import { doseToMinor, formatStoredDose } from '../../../lib/record-format.js';
import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { SelectField } from '../../ui/SelectField.js';
import { TextField } from '../../ui/TextField.js';
import styles from './insights.module.css';

const categories = [
    { value: 'motor', label: 'Motor' },
    { value: 'language', label: 'Language' },
    { value: 'social', label: 'Social' },
    { value: 'cognitive', label: 'Cognitive' },
    { value: 'first', label: 'First' },
    { value: 'other', label: 'Other' },
] as const;

const doseUnits = [
    { value: 'ml', label: 'ml' },
    { value: 'mg', label: 'mg' },
    { value: 'drops', label: 'drops' },
    { value: 'tablet', label: 'tablet' },
] as const;

const reminderKinds = [
    { value: 'feed', label: 'Feed' },
    { value: 'pump', label: 'Pump' },
    { value: 'medication', label: 'Medication' },
    { value: 'sleep', label: 'Sleep' },
    { value: 'custom', label: 'Custom' },
] as const;

export function MilestoneSection({
    familyId,
    childId,
    timeZone,
    canLog,
}: {
    familyId: string;
    childId: string;
    timeZone: string;
    canLog: boolean;
}): ReactElement {
    const milestones = useMilestones(familyId, childId);
    const photos = usePhotos(familyId, childId);
    const create = useCreateMilestone(familyId);
    const remove = useDeleteMilestone(familyId);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState<(typeof categories)[number]['value']>('first');
    const [achievedOn, setAchievedOn] = useState(calendarDateInZone(new Date(), timeZone));
    const [notes, setNotes] = useState('');
    const [photoId, setPhotoId] = useState('');

    function onSubmit(event: SubmitEvent<HTMLFormElement>): void {
        event.preventDefault();
        create.mutate(
            {
                childId,
                title,
                category,
                achievedOn,
                notes,
                ...(photoId.length > 0 ? { photoId } : {}),
            },
            {
                onSuccess: () => {
                    setTitle('');
                    setNotes('');
                    setPhotoId('');
                },
            },
        );
    }

    return (
        <Card title="Milestones" headingLevel={2}>
            {milestones.error ? <FormError message={errorText(milestones.error)} /> : null}
            {(milestones.data ?? []).length === 0 ? (
                <p className={styles.meta}>No milestones yet.</p>
            ) : (
                <ul className={styles.list}>
                    {(milestones.data ?? []).map((row) => (
                        <li key={row.id}>
                            <p>
                                {row.title} · {milestoneCategoryLabels[row.category]} ·{' '}
                                {formatCalendarDate(row.achievedOn)}
                            </p>
                            {row.photoId ? (
                                <img
                                    src={photoPath(row.photoId)}
                                    alt={row.title}
                                    className={styles.thumb}
                                />
                            ) : null}
                            {canLog ? (
                                <Button
                                    variant="danger"
                                    onClick={() => {
                                        remove.mutate(row.id);
                                    }}
                                >
                                    Delete
                                </Button>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}
            {canLog ? (
                <form className={fieldStyles.stack} onSubmit={onSubmit}>
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(event) => {
                            setTitle(event.target.value);
                        }}
                        required
                    />
                    <SelectField
                        label="Category"
                        options={categories}
                        value={category}
                        onChange={(event) => {
                            setCategory(event.target.value as (typeof categories)[number]['value']);
                        }}
                    />
                    <TextField
                        label="Achieved on"
                        type="date"
                        value={achievedOn}
                        onChange={(event) => {
                            setAchievedOn(event.target.value);
                        }}
                        required
                    />
                    <TextField
                        label="Milestone notes"
                        value={notes}
                        onChange={(event) => {
                            setNotes(event.target.value);
                        }}
                    />
                    {(photos.data?.length ?? 0) > 0 ? (
                        <SelectField
                            label="Photo"
                            options={[
                                { value: '', label: 'No photo' },
                                ...(photos.data ?? []).map((photo) => ({
                                    value: photo.id,
                                    label: photo.caption ?? 'Untitled photo',
                                })),
                            ]}
                            value={photoId}
                            onChange={(event) => {
                                setPhotoId(event.target.value);
                            }}
                        />
                    ) : null}
                    {create.error ? <FormError message={errorText(create.error)} /> : null}
                    <Button type="submit" loading={create.isPending}>
                        Save milestone
                    </Button>
                </form>
            ) : null}
        </Card>
    );
}

export function MedicationSection({
    familyId,
    childId,
    timeZone,
    canLog,
}: {
    familyId: string;
    childId: string;
    timeZone: string;
    canLog: boolean;
}): ReactElement {
    const medications = useMedications(familyId, childId);
    const doses = useDoses(familyId, childId);
    const create = useCreateMedication(familyId);
    const logDose = useCreateDose(familyId);
    const removeDose = useDeleteDose(familyId);
    const [name, setName] = useState('');
    const [unit, setUnit] = useState<DoseUnit>('ml');
    const [amount, setAmount] = useState('');
    const [hours, setHours] = useState('');

    function onSubmit(event: SubmitEvent<HTMLFormElement>): void {
        event.preventDefault();
        const defaultDoseAmount =
            amount.trim().length === 0 ? null : doseToMinor(Number(amount), unit);
        const minIntervalS = hours.trim().length === 0 ? null : Math.round(Number(hours) * 3600);
        create.mutate(
            {
                childId,
                name,
                doseUnit: unit,
                defaultDoseAmount,
                minIntervalS,
            },
            {
                onSuccess: () => {
                    setName('');
                    setAmount('');
                    setHours('');
                },
            },
        );
    }

    return (
        <Card title="Medication" headingLevel={2}>
            {medications.error ? <FormError message={errorText(medications.error)} /> : null}
            {(medications.data ?? []).length === 0 ? (
                <p className={styles.meta}>No medicines yet.</p>
            ) : (
                <ul className={styles.list}>
                    {(medications.data ?? []).map((row) => (
                        <li key={row.id}>
                            <p>
                                {row.name}
                                {row.defaultDoseAmount === null
                                    ? ''
                                    : ` · ${formatStoredDose(row.defaultDoseAmount, row.doseUnit)}`}
                                {row.active ? '' : ' · paused'}
                            </p>
                            {canLog && row.active ? (
                                <Button
                                    variant="secondary"
                                    loading={logDose.isPending}
                                    onClick={() => {
                                        logDose.mutate({ medicationId: row.id });
                                    }}
                                >
                                    Log dose
                                </Button>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}
            {(doses.data ?? []).length > 0 ? (
                <ul className={styles.list}>
                    {(doses.data ?? []).map((row) => (
                        <li key={row.id}>
                            <p>
                                {formatStoredDose(row.doseAmount, row.doseUnit)} ·{' '}
                                {formatWhen(row.givenAt, timeZone)}
                            </p>
                            {canLog ? (
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        removeDose.mutate(row.id);
                                    }}
                                >
                                    Remove dose
                                </Button>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : null}
            {canLog ? (
                <form className={fieldStyles.stack} onSubmit={onSubmit}>
                    <TextField
                        label="Medicine"
                        value={name}
                        onChange={(event) => {
                            setName(event.target.value);
                        }}
                        required
                    />
                    <SelectField
                        label="Unit"
                        options={doseUnits}
                        value={unit}
                        onChange={(event) => {
                            setUnit(event.target.value as DoseUnit);
                        }}
                    />
                    <TextField
                        label="Default amount"
                        inputMode="decimal"
                        value={amount}
                        onChange={(event) => {
                            setAmount(event.target.value);
                        }}
                    />
                    <TextField
                        label="Minimum hours between doses"
                        inputMode="decimal"
                        value={hours}
                        onChange={(event) => {
                            setHours(event.target.value);
                        }}
                    />
                    {create.error ? <FormError message={errorText(create.error)} /> : null}
                    {logDose.error ? <FormError message={errorText(logDose.error)} /> : null}
                    <Button type="submit" loading={create.isPending}>
                        Save medicine
                    </Button>
                </form>
            ) : null}
        </Card>
    );
}

export function ReminderSection({
    familyId,
    childId,
    canLog,
}: {
    familyId: string;
    childId: string;
    canLog: boolean;
}): ReactElement {
    const reminders = useReminders(familyId, undefined);
    const create = useCreateReminder(familyId);
    const update = useUpdateReminder(familyId);
    const [title, setTitle] = useState('');
    const [kind, setKind] = useState<ReminderKind>('feed');
    const [mode, setMode] = useState<'interval' | 'time'>('interval');
    const [hours, setHours] = useState('3');
    const [timeOfDay, setTimeOfDay] = useState('07:30');

    function onSubmit(event: SubmitEvent<HTMLFormElement>): void {
        event.preventDefault();
        const needsChild = kind === 'feed' || kind === 'sleep' || kind === 'medication';
        create.mutate(
            {
                childId: needsChild ? childId : null,
                kind,
                title,
                ...(mode === 'interval'
                    ? { intervalS: Math.round(Number(hours) * 3600) }
                    : { timeOfDay }),
            },
            {
                onSuccess: () => {
                    setTitle('');
                },
            },
        );
    }

    return (
        <Card title="Reminders" headingLevel={2}>
            <p className={styles.meta}>A scheduled check adds these to Today when they are due.</p>
            {reminders.error ? <FormError message={errorText(reminders.error)} /> : null}
            <ul className={styles.list}>
                {(reminders.data ?? []).map((row) => (
                    <li key={row.id}>
                        <p>
                            {row.title} · {reminderKindLabels[row.kind]}
                            {row.intervalS !== null
                                ? ` · every ${Math.round(row.intervalS / 3600)}h`
                                : ''}
                            {row.timeOfDay !== null ? ` · ${row.timeOfDay}` : ''}
                        </p>
                        {canLog ? (
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    checked={row.enabled}
                                    aria-label={`${row.title} reminder`}
                                    disabled={update.isPending}
                                    onChange={() => {
                                        update.mutate({
                                            id: row.id,
                                            body: { enabled: !row.enabled },
                                        });
                                    }}
                                />
                                {row.enabled ? 'On' : 'Off'}
                            </label>
                        ) : (
                            <p className={styles.meta}>{row.enabled ? 'On' : 'Off'}</p>
                        )}
                    </li>
                ))}
            </ul>
            {canLog ? (
                <form className={fieldStyles.stack} onSubmit={onSubmit}>
                    <TextField
                        label="Reminder"
                        value={title}
                        onChange={(event) => {
                            setTitle(event.target.value);
                        }}
                        required
                    />
                    <SelectField
                        label="Kind"
                        options={reminderKinds}
                        value={kind}
                        onChange={(event) => {
                            setKind(event.target.value as ReminderKind);
                        }}
                    />
                    <SelectField
                        label="Schedule"
                        options={[
                            { value: 'interval', label: 'Every few hours' },
                            { value: 'time', label: 'At a time of day' },
                        ]}
                        value={mode}
                        onChange={(event) => {
                            setMode(event.target.value === 'time' ? 'time' : 'interval');
                        }}
                    />
                    {mode === 'interval' ? (
                        <TextField
                            label="Hours"
                            inputMode="decimal"
                            value={hours}
                            onChange={(event) => {
                                setHours(event.target.value);
                            }}
                            required
                        />
                    ) : (
                        <TextField
                            label="Time"
                            type="time"
                            value={timeOfDay}
                            onChange={(event) => {
                                setTimeOfDay(event.target.value);
                            }}
                            required
                        />
                    )}
                    {create.error ? <FormError message={errorText(create.error)} /> : null}
                    <Button type="submit" loading={create.isPending}>
                        Save reminder
                    </Button>
                </form>
            ) : null}
        </Card>
    );
}

export function ExportSection({
    familyId,
    familyName,
}: {
    familyId: string;
    familyName: string;
}): ReactElement {
    const download = useFamilyExport(familyId);
    return (
        <Card title="Export" headingLevel={2}>
            <p className={styles.meta}>Download this family’s records as JSON.</p>
            {download.error ? <FormError message={errorText(download.error)} /> : null}
            <Button
                variant="secondary"
                loading={download.isPending}
                onClick={() => {
                    download.mutate(undefined, {
                        onSuccess: (data) => {
                            const blob = new Blob([JSON.stringify(data, null, 2)], {
                                type: 'application/json',
                            });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${familyName.replaceAll(/[^\w.-]+/g, '-')}-export.json`;
                            link.click();
                            URL.revokeObjectURL(url);
                        },
                    });
                }}
            >
                Download JSON
            </Button>
        </Card>
    );
}
